import React, { useState, useCallback, useEffect } from "react";
import TextField from "./TextField";
import { SxProps, Theme } from "@mui/material";

interface NumberInputProps {
    value: number;
    onChange: (value: number) => void;
    format?: {
        prefix?: string; // Optional prefix
        postfix?: string; // Optional postfix
    };
    minValue?: number; // Optional minimum value
    maxLength?: number; // Optional maximum length
    supportDecimal?: boolean; // Optional support for decimal numbers
    selectValueOnFocus?: boolean; // Optional support to select the value of focus for immediate replacement
    variant?: "contained" | "outlined" | "underlined"; // Optional variant
    width?: number; // Optional width
    sx?: SxProps<Theme>; // Optional style
    onEnter?: () => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    nextRef?: React.RefObject<HTMLInputElement>;
    disabled?: boolean;
}

const NumberInput = React.forwardRef((props: NumberInputProps, ref) => {
    const {
        value,
        onChange,
        format = {},
        minValue = 0,
        maxLength = 10,
        supportDecimal = false,
        selectValueOnFocus = false,
        variant,
        width,
        sx,
        onEnter,
        onKeyDown,
        nextRef,
        disabled,
    } = props;

    const [displayValue, setDisplayValue] = useState<string>(
        value ? value.toString() : ""
    );
    const [isFocused, setIsFocused] = useState<boolean>(false);

    // Sync internal displayValue with external value prop
    useEffect(() => {
        if (!isNaN(value)) setDisplayValue(value.toString());
    }, [value]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value;
            const validationRegex = supportDecimal
                ? /^\d*\.?\d{0,2}$/
                : /^\d*$/;

            // Allow only digits and ensure length is within 3 characters
            if (validationRegex.test(value) && value.length <= maxLength) {
                setDisplayValue(value);
                const num = parseFloat(value);
                if (!isNaN(num)) {
                    onChange(Math.abs(num));
                }
            }
        },
        [maxLength, supportDecimal, onChange]
    );

    const handleBlur = useCallback(() => {
        setIsFocused(false);
        const numValue = parseFloat(displayValue);

        if (displayValue === "" || isNaN(numValue) || numValue < minValue) {
            onChange(minValue); // Set to default if invalid
            setDisplayValue(minValue.toString());
        } else {
            onChange(numValue); // Update value with valid value
        }
    }, [displayValue, minValue, onChange]);

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            if (selectValueOnFocus) {
                setTimeout(() => e.target.select(), 0); // Ensure selection on focus
            }
            setIsFocused(true);
        },
        [selectValueOnFocus]
    );

    return (
        <TextField
            ref={ref}
            value={
                isFocused
                    ? displayValue
                    : `${format.prefix || ""} ${value} ${format.postfix || ""}`
            }
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onEnter={onEnter}
            onKeyDown={onKeyDown}
            variant={variant}
            width={width}
            sx={sx}
            nextRef={nextRef}
            disabled={disabled}
        />
    );
});

export default NumberInput;
